import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { parseDate } from "@lyfegen-platform/utils";
import { addDays, compareAsc, differenceInDays, format, isBefore } from "date-fns";
import { connect, useFieldValue, useSetFieldValue } from "formik";
import { useEffect, useRef } from "react";
import * as Yup from "yup";
import { FlexContext } from "../models/flexible/flex-context";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { neo4jDateToDate } from "../utils/neo4j-date-to-date";
import { GeneralFieldController } from "./general-field-controller";

const FieldRelationsEffect = connect(function (props: { context: FlexContext }) {
	const [responseReceived] = useFieldValue(AttributeIdentifiers.ResponseReceived);
	const [therapyStartDate] = useFieldValue(AttributeIdentifiers.TherapyStartDate);
	const setFieldValue = useSetFieldValue();
	const responseReceivedRef = useRef(responseReceived);
	const therapyStartDateRef = useRef(therapyStartDate);

	useEffect(() => {
		if (!responseReceived && responseReceivedRef.current) {
			setFieldValue(AttributeIdentifiers.TherapyStartDate, "");
			setFieldValue(AttributeIdentifiers.PatientEnrolledInTreatment, "");
			setFieldValue(AttributeIdentifiers.ReasonForDiscontinuation, "");
			setFieldValue(AttributeIdentifiers.Result, "");
			setFieldValue(AttributeIdentifiers.NumberOfPrescriptionsToDate, "");
			const adverseEventsSection = props.context.sectionControllers.find((section) =>
				section.fieldControllers.some((field) => field.schema.attribute.identifier === AttributeIdentifiers.AdverseEvent)
			);
			if (adverseEventsSection) {
				setFieldValue(adverseEventsSection?.schema.localizableKey, []);
			}
			setFieldValue(AttributeIdentifiers.ResponseAssessmentDate, format(new Date(), "yyyy-MM-dd"));
		}
		responseReceivedRef.current = responseReceived;
	}, [props.context.sectionControllers, responseReceived, setFieldValue]);

	const startingPointData = props.context.startingPoint?.data;

	useEffect(() => {
		if (startingPointData && therapyStartDate && therapyStartDateRef.current !== therapyStartDate) {
			const defaultAssessmentDate =
				startingPointData && startingPointData.therapyTrialStartDate && startingPointData.therapyTrialEndDate && therapyStartDate
					? format(
							[
								new Date(),
								addDays(
									parseDate(therapyStartDate),
									differenceInDays(startingPointData.therapyTrialEndDate, startingPointData.therapyTrialStartDate)
								),
							].sort(compareAsc)[0],
							"yyyy-MM-dd"
					  )
					: format(new Date(), "yyyy-MM-dd");
			setFieldValue(AttributeIdentifiers.ResponseAssessmentDate, defaultAssessmentDate);
		}
		therapyStartDateRef.current = therapyStartDate;
	}, [setFieldValue, startingPointData, therapyStartDate]);

	return null;
});

export class ResponseAssessmentDateFieldController extends GeneralFieldController {
	override createValidationRule(initialData: unknown, translate: (key: string) => string) {
		return (super.createFieldValidationRuleByDataType(translate) as Yup.DateSchema)
			.test("is-after-start-date", translate("must-be-after-therapy-start-date"), (v, ctx) =>
				v ? !isBefore(v, ctx.parent.therapyStartDate) : false
			)
			.max(new Date(), translate("cannot-be-greater-than-today"));
	}

	renderBeforeContent(): React.ReactElement {
		return <FieldRelationsEffect context={this.parent.context} />;
	}

	override useControlProps(): FlexControlProps {
		const [therapyStartDate] = useFieldValue(AttributeIdentifiers.TherapyStartDate);
		const minAssessmentDate = therapyStartDate;
		const maxAssessmentDate = new Date();

		return {
			minDate: neo4jDateToDate(minAssessmentDate),
			maxDate: neo4jDateToDate(maxAssessmentDate),
		};
	}
}
